import { useMemo } from 'react'

import type { api } from './api'
import type { Employee } from './employees'
import { useSession } from './SessionContext'

/**
 * รายงาน "ข้อมูลบุคคล" รายคน — คุยกับ /api/admin/person
 *
 * รวมของที่ระบบ HR เดิมแยกไว้ 4 หน้า (Home.aspx · Leave.aspx?action=1 ·
 * Leave.aspx?action=2 · TimeAttendance.aspx) ไว้ในหน้าเดียว
 *
 * ⚠️ ทุกอย่างมาจาก backend ของโปรเจกต์นี้เท่านั้น — ไม่มีการเรียก API ของระบบเดิม
 *    (ระบบนั้นจะถูกปิด เส้นที่นี่ต้องยืนได้เอง)
 */

export const PERSON_BASE = '/api/admin/person'

/** สิทธิ์ลา 1 ประเภท — union ของโควตารายคนกับ master ของประเภท */
export type LeaveQuota = {
  leaveType: string
  desc: string
  remark: string | null
  /** null = ไม่ได้กำหนดเพดานไว้ ไม่ใช่ 0 */
  entitlementDays: number | null
  /** สิทธิ์นี้มาจากไหน — โควตารายคน หรือค่ากลางของประเภท */
  entitlementFrom: 'HR_CAN_LEAVE' | 'HR_LEAVE_TYPE' | null
  dayUsed: number
  hoursUsed: number
  dayRemain: number | null
  hasQuotaRow: boolean
  usedThisYear: boolean
  /** เคยลาประเภทนี้ แต่ประเภทหายไปจาก master แล้ว */
  missingFromMaster?: true
}

/**
 * ใบลา / ใบปรับปรุงเวลา — รูปร่างเดียวกัน ต่างกันที่ leaveType `999`
 * (ชื่อฟิลด์ตาม shapeForm ใน backend/src/lib/leave.js)
 *
 * ⚠️ `hours` เป็น **ชั่วโมง** ไม่ใช่นาที แม้คอลัมน์ใน DB จะชื่อ TOTAL_MINUTE
 *    ลาเต็มวัน = 8 · ครึ่งวัน = 4 → แปลงเป็นวันด้วย /8 ห้ามหาร 60
 * ⚠️ `dateStart` เป็น null ได้ในใบแก้ไข "เวลาออก" — ใช้ `workDate` เป็นวันของใบแทน
 */
export type LeaveForm = {
  recId: number | string
  leaveType: string
  desc: string
  workDate: string | null
  dateStart: string | null
  dateEnd: string | null
  hours: number | null
  reason: string | null
  status: string
  statusText: string
  statusTone: string
  hrApproved: boolean
  approvedByChief: boolean
  approveReason: string | null
  createdAt: string | null
  /** นับเป็นสิทธิ์ที่ใช้ไปแล้วไหม (CANCEL/REJECT ไม่นับ) */
  counted: boolean
}

export type MonthStat = {
  month: string
  monthNo: number
  isFuture: boolean
  hasData: boolean
  summary: WorkSummary
}

export type WorkSummary = {
  days: number
  workDays: number
  holidays: number
  noTimeRequiredDays: number
  completeDays: number
  missingDays: number
  missingInDays: number
  missingOutDays: number
  absentDays: number
  suspectDays: number
  lateDays: number
  lateMinutes: number
  leaveDays: number
  adjustedDays: number
  timeEditDays: number
}

export type PersonYear = {
  employee: Employee
  year: number
  years: number[]
  leave: {
    quota: LeaveQuota[]
    leaves: LeaveForm[]
    adjustments: LeaveForm[]
    summary: {
      leaveCount: number
      adjustmentCount: number
      leaveHours: number
      leaveDays: number
      truncated: boolean
    }
  }
  work: { months: MonthStat[]; summary: WorkSummary; today: string }
}

export type WorkDay = {
  workDate: string
  dow: number
  isHoliday: boolean
  noTimeRequired: boolean
  shiftName: string | null
  shiftStart: string | null
  shiftEnd: string | null
  actualStart: string | null
  actualEnd: string | null
  /** เวลาที่เครื่องสแกนบันทึกไว้ล้วน ๆ — คอลัมน์ "เข้าจริง/ออกจริง" ต้องใช้คู่นี้
   *  ไม่ใช่ inTime/outTime ซึ่งเป็นเวลาหลัง HR แก้ (ใช้คิดเงินเดือน) */
  punchIn: string | null
  punchOut: string | null
  /** เวลาที่ใช้จริงต่างจากที่ตอก = วันนั้นถูกแก้เวลา */
  correctedIn: boolean
  correctedOut: boolean
  adjustStart: string | null
  adjustEnd: string | null
  inTime: string | null
  outTime: string | null
  outDayOffset: number
  /** Adjust_* ต่างจากเวลาตอกจริง = ถูกแก้จริง (ไม่ใช่แค่ 'มีค่า' — 75% เป็นสำเนา) */
  adjustedIn: boolean
  adjustedOut: boolean
  adjusted: boolean
  timeSuspect: boolean
  lateMinute: number
  late: boolean
  onLeave: boolean
  /** ประเภทลาของวันนั้น — ใบเดียวได้ชื่อนั้น หลายใบได้ชื่อแรก (backend ตั้งให้) */
  leaveTypeDesc: string | null
  /** ใบลาที่กินวันนี้ · ครึ่งวันจะมี from/to ไม่เต็มกะ */
  leaves: {
    leaveType: string
    leaveTypeDesc: string
    from: string | null
    to: string | null
    docHours: number
    approved: boolean
    statusText: string
  }[]
  absent: boolean
  missingIn: boolean
  missingOut: boolean
  /** ใบปรับปรุงเวลา (type 999) ของวันนั้น — คอลัมน์ "ปรับปรุงเข้า/เลิก" (hr: FROM_DATE3/TO_DATE3) */
  timeEdits: { from: string | null; to: string | null; toNextDay?: boolean; approved: boolean }[]
  workDayRemark: string | null
}

/** OT 1 เอกสาร (ชื่อฟิลด์ตาม backend/src/lib/ot.js) */
export type OtItem = {
  rowId: string | number
  otDate: string
  otShiftText: string
  isHoliday: boolean
  timeStart: string | null
  timeEnd: string | null
  startsNextDay: boolean
  endsNextDay: boolean
  minutes: number
  hours: number
  /** null = เอกสารเดียวมีหลายเรท ให้ดูที่ `rates` แทน */
  rate: number | null
  rates: { rate: number; minutes: number }[]
  statusCode: string
  statusText: string
  statusGroup: 'approved' | 'pending' | 'rejected'
  /** true = ไม่มี detail ต้องประมาณจากเวลาเริ่ม–สิ้นสุด */
  estimated: boolean
  remark: string | null
}

export type PersonMonth = {
  employee: { empCode: string; fullName: string | null }
  month: string
  monthsAvailable: string[]
  today: string
  isFuture: boolean
  work: { days: WorkDay[]; summary: WorkSummary; range: { from: string; to: string } }
  ot: { items: OtItem[]; summary: unknown }
}

const qs = (o: Record<string, string | number | undefined>) => {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(o)) if (v !== undefined && v !== '') p.set(k, String(v))
  const s = p.toString()
  return s ? `?${s}` : ''
}

/** ⚠️ ต้องผ่าน `call` ของ SessionContext เสมอ — ที่เดียวที่ดัก 401 ให้ทั้งแอป */
export function personApi(call: typeof api) {
  return {
    year: (empCode: string, year?: number) =>
      call<PersonYear>(`${PERSON_BASE}/${encodeURIComponent(empCode)}${qs({ year })}`),

    month: (empCode: string, month?: string) =>
      call<PersonMonth>(`${PERSON_BASE}/${encodeURIComponent(empCode)}/month${qs({ month })}`),
  }
}

export function usePerson() {
  const { call } = useSession()
  return useMemo(() => personApi(call), [call])
}

/* ── ตัวช่วยแสดงผล ─────────────────────────────────────────────────────────── */

/**
 * ตัวจัดรูปแบบทั้งหมดอยู่ใน personFormat.ts — เป็นฟังก์ชันล้วนที่ไม่ import อะไรเลย
 * จึงรันทดสอบนอกเบราว์เซอร์ได้ (ไฟล์นี้ import React จึงรันตรงไม่ได้)
 * re-export ต่อเพื่อให้หน้าจอ import จากที่เดียวเหมือนเดิม
 */
export {
  MONTH_TH,
  dayHourMin,
  dowText,
  humanText,
  leaveRange,
  dayNum,
  minutesText,
  errorText,
} from './personFormat'
export type { LeaveRange } from './personFormat'
